import React, { useEffect, useRef, useState } from 'react';
import './FieldPreview.css';

interface Props {
  /** Nom du fichier dans public/apercus/ (sans le chemin). */
  apercu: string;
  /** Où ça se trouve sur le site public — titre de l'infobulle. */
  titre: string;
  /** Ce que le champ est censé contenir. */
  description: string;
  /** Exemple rédigé, pour les zones de texte : décrire ce qu'on attend ne
   *  suffit pas, montrer une formulation débloque la page blanche. */
  exemple?: string;
}

/**
 * Petite icône à côté d'un libellé : au clic, montre une capture du site
 * public à l'endroit où ce champ atterrit.
 *
 * Les intitulés du formulaire ne disent pas où va ce qu'on écrit — « Texte
 * d'introduction » ou « Description des caractéristiques » ne correspondent à
 * aucun titre visible sur le site. Montrer le résultat vaut mieux que le
 * décrire.
 *
 * Les captures viennent du site de Shelter, produites par
 * `e2e/scripts/capturer-apercus.mjs`.
 */
const FieldPreview: React.FC<Props> = ({ apercu, titre, description, exemple }) => {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    const auClic = (e: MouseEvent) => {
      if (conteneur.current && !conteneur.current.contains(e.target as Node)) {
        setOuvert(false);
      }
    };
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  return (
    <span className="apercu" ref={conteneur}>
      <button
        type="button"
        className="apercu-declencheur"
        aria-expanded={ouvert}
        // Sans guillemets autour de `titre` : plusieurs titres en contiennent
        // déjà, et l'emboîtement donnait « Bloc « Location » » à l'oral.
        aria-label={`Aperçu sur le site public — ${titre}`}
        onClick={() => setOuvert((v) => !v)}
      >
        {/* Icône « image » en SVG : aucune dépendance de pictogrammes dans ce
            dépôt, et un caractère unicode rendrait différemment selon l'OS. */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" aria-hidden>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="8.5" cy="9.5" r="1.5" />
          <path d="M21 16l-5-5L5 20" />
        </svg>
      </button>

      {ouvert && (
        <span className="apercu-bulle" role="dialog" aria-label={titre}>
          <span className="apercu-bulle-titre">{titre}</span>
          <span className="apercu-bulle-texte">{description}</span>
          {exemple && (
            <span className="apercu-bulle-exemple">
              <em>Exemple —</em> {exemple}
            </span>
          )}
          {/* L'image n'est demandée qu'à l'ouverture : sept captures chargées
              d'office alourdiraient chaque visite de la page pour rien. */}
          <img
            src={`${process.env.PUBLIC_URL}/apercus/${apercu}`}
            alt={`Aperçu de la section « ${titre} » sur le site public`}
            loading="lazy"
          />
          <span className="apercu-bulle-note">Exemple : le site de The Shelter.</span>
        </span>
      )}
    </span>
  );
};

export default FieldPreview;
